// Project card used by the Grid kanban. Title + avatar stack on top,
// optional secondary tag (e.g. priority chip when grouped by department),
// date range, priority-tinted progress bar, completion stat + health pill.

import { useMemo } from 'react';

import { AppUser, Project, Task } from '../../pm/types';
import { formatMonthDay } from '../../pm/dueDate';
import {
  DEFAULT_DEPARTMENTS,
  DepartmentDef,
  PROJECT_PRIORITY_COLORS,
  PROJECT_PRIORITY_LABELS,
  PROJECT_PRIORITY_SOFT,
} from '../../pm/constants';
import { computeHealth } from '../../pm/health';
import AvatarStack from './AvatarStack';
import HealthPill from './HealthPill';

interface SecondaryTag {
  dot: string;
  label: string;
}

interface Props {
  project: Project;
  tasks: Task[];        // pre-filtered to this project
  admins: AppUser[];
  /** Tint used for the progress fill + card accent. Usually the priority
   *  color so the importance signal stays consistent across groupings.    */
  accentColor: string;
  /** Small chip shown above the meta line, used when the *other* dimension
   *  matters (e.g. priority chip when the lane is a department).         */
  secondaryTag?: SecondaryTag | null;
  onOpen: () => void;
}

const ProjectCard: React.FC<Props> = ({
  project,
  tasks,
  admins,
  accentColor,
  secondaryTag,
  onOpen,
}) => {
  // Derive avatar stack from the union of task owners — projects don't have
  // explicit "members" in our schema, so anyone who owns a task implicitly
  // appears on the card.
  const memberEmails = useMemo(() => {
    const uidSet = new Set<string>();
    for (const t of tasks) {
      if (t.ownerId) uidSet.add(t.ownerId);
    }
    const emails: string[] = [];
    uidSet.forEach((uid) => {
      const u = admins.find((a) => a.uid === uid);
      if (u?.email) emails.push(u.email);
    });
    return emails;
  }, [tasks, admins]);

  const total = tasks.length;
  const done = tasks.filter((t) => t.status === 'done').length;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  const scheduled = typeof project.startDate === 'number' && typeof project.targetDate === 'number';
  // Health is per-card; cheap enough to compute on every render since each
  // call only loops over the project's own tasks.
  const health = useMemo(() => computeHealth(project, tasks), [project, tasks]);
  const dateRange = scheduled
    ? `${formatMonthDay(project.startDate as number)} – ${formatMonthDay(project.targetDate as number)}`
    : 'Unscheduled';
  const softBg = project.priority
    ? PROJECT_PRIORITY_SOFT[project.priority]
    : 'transparent';

  return (
    <div
      className="pm-proj-card"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      style={{ ['--pm-card-accent' as string]: accentColor }}
    >
      <div className="pm-proj-card-top">
        <h3 className="pm-proj-card-title">{project.title}</h3>
        <AvatarStack emails={memberEmails} max={3} />
      </div>
      {secondaryTag ? (
        <span className="pm-proj-card-tag">
          <span className="pm-pri-dot" style={{ background: secondaryTag.dot }} />
          {secondaryTag.label}
        </span>
      ) : null}
      <p className="pm-proj-card-meta">{dateRange}</p>
      <div className="pm-proj-card-bar" style={{ background: softBg }}>
        <i style={{ width: `${pct}%`, background: accentColor }} />
      </div>
      <div className="pm-proj-card-foot">
        <span className="pm-proj-card-numbers">
          <b>{done}</b>/{total} · {pct}%
        </span>
        {!health.isUnscheduled || health.manual ? (
          <HealthPill health={health} />
        ) : null}
      </div>
    </div>
  );
};

// Re-export helpers so the GridView can build secondary tags without
// importing from constants directly.
export function priorityTag(priority?: string | null): SecondaryTag | null {
  if (!priority) return null;
  const key = priority as keyof typeof PROJECT_PRIORITY_LABELS;
  if (!PROJECT_PRIORITY_LABELS[key]) return null;
  return { dot: PROJECT_PRIORITY_COLORS[key], label: PROJECT_PRIORITY_LABELS[key] };
}

/** Optional second argument lets callers pass a merged (built-in + custom)
 *  department list; otherwise we fall back to the built-ins so prior call
 *  sites don't break. */
export function departmentTag(
  department?: string | null,
  fromList?: DepartmentDef[],
): SecondaryTag | null {
  if (!department) return null;
  const list = fromList ?? DEFAULT_DEPARTMENTS;
  const d = list.find((x) => x.id === department);
  if (!d) return null;
  return { dot: d.swatch, label: d.label };
}

export default ProjectCard;
